import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Film, RefreshCw } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { filmsApi, adminApi, formatDuration } from '../lib/api';
import AgreementsPanel from '../components/AgreementsPanel';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';

const AdminPage = () => {
  const { isAdmin, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [films, setFilms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFilm, setEditingFilm] = useState(null);
  const [seeding, setSeeding] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    youtube_id: '',
    thumbnail: '',
    creator: '',
    duration: 0,
    year: new Date().getFullYear(),
    genres: '',
    categories: '',
    featured: false,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      navigate('/browse');
      toast.error('Admin access required');
      return;
    }

    fetchFilms();
  }, [isAuthenticated, isAdmin, navigate]);

  const fetchFilms = async () => {
    try {
      const data = await filmsApi.getAll({ limit: 100 });
      setFilms(data);
    } catch (error) {
      console.error('Failed to fetch films:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const result = await adminApi.seedDatabase();
      toast.success(result.message);
      fetchFilms();
    } catch (error) {
      toast.error(error.message || 'Failed to seed database');
    } finally {
      setSeeding(false);
    }
  };

  const handleOpenDialog = (film = null) => {
    if (film) {
      setEditingFilm(film);
      setFormData({
        title: film.title,
        description: film.description,
        youtube_id: film.youtube_id,
        thumbnail: film.thumbnail,
        creator: film.creator,
        duration: film.duration,
        year: film.year,
        genres: film.genres.join(', '),
        categories: film.categories.join(', '),
        featured: film.featured,
      });
    } else {
      setEditingFilm(null);
      setFormData({
        title: '',
        description: '',
        youtube_id: '',
        thumbnail: '',
        creator: '',
        duration: 0,
        year: new Date().getFullYear(),
        genres: '',
        categories: '',
        featured: false,
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.youtube_id) {
      toast.error('Title and YouTube ID are required');
      return;
    }

    const filmData = {
      ...formData,
      genres: formData.genres.split(',').map((g) => g.trim()).filter(Boolean),
      categories: formData.categories.split(',').map((c) => c.trim()).filter(Boolean),
      thumbnail: formData.thumbnail || `https://img.youtube.com/vi/${formData.youtube_id}/maxresdefault.jpg`,
    };

    try {
      if (editingFilm) {
        await adminApi.updateFilm(editingFilm.film_id, filmData);
        toast.success('Film updated');
      } else {
        await adminApi.createFilm(filmData);
        toast.success('Film created');
      }
      setDialogOpen(false);
      fetchFilms();
    } catch (error) {
      toast.error(error.message || 'Failed to save film');
    }
  };

  const handleDelete = async (filmId) => {
    if (!window.confirm('Are you sure you want to delete this film?')) return;

    try {
      await adminApi.deleteFilm(filmId);
      toast.success('Film deleted');
      setFilms(films.filter((f) => f.film_id !== filmId));
    } catch (error) {
      toast.error(error.message || 'Failed to delete film');
    }
  };

  // Auto-generate thumbnail from YouTube ID
  const handleYoutubeIdChange = (value) => {
    setFormData({
      ...formData,
      youtube_id: value,
      thumbnail: value ? `https://img.youtube.com/vi/${value}/maxresdefault.jpg` : '',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030014] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030014]" data-testid="admin-page">
      <Navbar />

      <div className="pt-24 pb-12 max-w-[1600px] mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1
              className="text-3xl md:text-4xl font-bold tracking-tighter"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Admin Panel
            </h1>
            <p className="text-gray-400 mt-2">{films.length} films in library</p>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={handleSeed}
              disabled={seeding}
              variant="outline"
              className="bg-[#0F0B1E] border-[#00FF9D]/30 text-[#00FF9D] hover:bg-[#00FF9D]/10"
              data-testid="seed-button"
            >
              {seeding ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Seed Database
            </Button>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  onClick={() => handleOpenDialog()}
                  className="btn-primary"
                  data-testid="add-film-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Film
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0F0B1E] border-[#8B5CF6]/30 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {editingFilm ? 'Edit Film' : 'Add New Film'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Title *</label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Film title"
                        className="bg-[#030014] border-[#8B5CF6]/20"
                        data-testid="film-title-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">YouTube ID *</label>
                      <Input
                        value={formData.youtube_id}
                        onChange={(e) => handleYoutubeIdChange(e.target.value)}
                        placeholder="e.g., dQw4w9WgXcQ"
                        className="bg-[#030014] border-[#8B5CF6]/20"
                        data-testid="film-youtube-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Description</label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Film description"
                      className="bg-[#030014] border-[#8B5CF6]/20 min-h-[100px]"
                      data-testid="film-description-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Creator</label>
                      <Input
                        value={formData.creator}
                        onChange={(e) => setFormData({ ...formData, creator: e.target.value })}
                        placeholder="Creator name"
                        className="bg-[#030014] border-[#8B5CF6]/20"
                        data-testid="film-creator-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Thumbnail URL</label>
                      <Input
                        value={formData.thumbnail}
                        onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                        placeholder="Auto-generated from YouTube ID"
                        className="bg-[#030014] border-[#8B5CF6]/20"
                        data-testid="film-thumbnail-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Duration (seconds)</label>
                      <Input
                        type="number"
                        value={formData.duration}
                        onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })}
                        placeholder="300"
                        className="bg-[#030014] border-[#8B5CF6]/20"
                        data-testid="film-duration-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Year</label>
                      <Input
                        type="number"
                        value={formData.year}
                        onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) || new Date().getFullYear() })}
                        placeholder="2025"
                        className="bg-[#030014] border-[#8B5CF6]/20"
                        data-testid="film-year-input"
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <Switch
                        checked={formData.featured}
                        onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
                        data-testid="film-featured-switch"
                      />
                      <label className="text-sm">Featured</label>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Genres (comma-separated)</label>
                      <Input
                        value={formData.genres}
                        onChange={(e) => setFormData({ ...formData, genres: e.target.value })}
                        placeholder="Sci-Fi, Drama, Action"
                        className="bg-[#030014] border-[#8B5CF6]/20"
                        data-testid="film-genres-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Categories (comma-separated)</label>
                      <Input
                        value={formData.categories}
                        onChange={(e) => setFormData({ ...formData, categories: e.target.value })}
                        placeholder="New Arrivals, Popular, Shorts"
                        className="bg-[#030014] border-[#8B5CF6]/20"
                        data-testid="film-categories-input"
                      />
                    </div>
                  </div>

                  {/* Thumbnail Preview */}
                  {formData.thumbnail && (
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Thumbnail Preview</label>
                      <img
                        src={formData.thumbnail}
                        alt="Preview"
                        className="w-full max-w-md rounded-lg"
                        onError={(e) => (e.target.style.display = 'none')}
                      />
                    </div>
                  )}

                  <Button
                    onClick={handleSubmit}
                    className="w-full btn-primary"
                    data-testid="save-film-button"
                  >
                    {editingFilm ? 'Update Film' : 'Create Film'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Films Table */}
        {films.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="films-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-2 text-sm font-medium text-gray-400">Thumbnail</th>
                  <th className="text-left py-4 px-2 text-sm font-medium text-gray-400">Title</th>
                  <th className="text-left py-4 px-2 text-sm font-medium text-gray-400">Creator</th>
                  <th className="text-left py-4 px-2 text-sm font-medium text-gray-400">Duration</th>
                  <th className="text-left py-4 px-2 text-sm font-medium text-gray-400">Views</th>
                  <th className="text-left py-4 px-2 text-sm font-medium text-gray-400">Rating</th>
                  <th className="text-left py-4 px-2 text-sm font-medium text-gray-400">Featured</th>
                  <th className="text-right py-4 px-2 text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {films.map((film) => (
                  <tr
                    key={film.film_id}
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    data-testid={`film-row-${film.film_id}`}
                  >
                    <td className="py-3 px-2">
                      <img
                        src={film.thumbnail}
                        alt={film.title}
                        className="w-24 h-14 object-cover rounded"
                      />
                    </td>
                    <td className="py-3 px-2">
                      <p className="font-medium line-clamp-1">{film.title}</p>
                      <p className="text-xs text-gray-400">{film.youtube_id}</p>
                    </td>
                    <td className="py-3 px-2 text-sm text-gray-300">{film.creator}</td>
                    <td className="py-3 px-2 text-sm text-gray-300">{formatDuration(film.duration)}</td>
                    <td className="py-3 px-2 text-sm text-gray-300">{film.views.toLocaleString()}</td>
                    <td className="py-3 px-2 text-sm">
                      <span className="text-[#8B5CF6]">{film.avg_rating.toFixed(1)} ★</span>
                    </td>
                    <td className="py-3 px-2">
                      {film.featured && (
                        <span className="badge text-xs">Featured</span>
                      )}
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleOpenDialog(film)}
                          className="p-2 hover:bg-[#8B5CF6]/20 rounded-lg transition-colors"
                          data-testid={`edit-film-${film.film_id}`}
                        >
                          <Edit className="w-4 h-4 text-[#8B5CF6]" />
                        </button>
                        <button
                          onClick={() => handleDelete(film.film_id)}
                          className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                          data-testid={`delete-film-${film.film_id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-20">
            <Film className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <h2 className="text-xl font-semibold mb-2">No films yet</h2>
            <p className="text-gray-400 mb-6">
              Click "Seed Database" to add sample films or "Add Film" to create one
            </p>
          </div>
        )}

        {/* Agreements Section */}
        <div className="mt-12">
          <AgreementsPanel />
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
